import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { KineticLines } from "@/components/KineticLines";

const PANELS = [
  {
    to: "/residential",
    testid: "entry-residential",
    title: "RESIDENTIAL",
    sub: "Siding for your home — installation, replacement & storm repair",
    position: "38% center",
    image: "https://images.pexels.com/photos/7601153/pexels-photo-7601153.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85",
  },
  {
    to: "/commercial",
    testid: "entry-commercial",
    title: "COMMERCIAL",
    sub: "For general contractors, developers & property managers",
    image: "https://images.pexels.com/photos/10486073/pexels-photo-10486073.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85",
  },
];

const TiltPanel = ({ panel, i }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [4, -4]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-5, 5]), { stiffness: 120, damping: 18 });
  const imgX = useSpring(useTransform(mx, [0, 1], ["-3.5%", "0%"]), { stiffness: 90, damping: 20 });
  const imgY = useSpring(useTransform(my, [0, 1], ["-3.5%", "0%"]), { stiffness: 90, damping: 20 });

  // Phone tilt: Android fires deviceorientation without a permission prompt; on iOS it stays gracefully still
  useEffect(() => {
    if (!window.matchMedia("(pointer: coarse)").matches) return;
    const onTilt = (e) => {
      if (e.gamma == null || e.beta == null) return;
      mx.set(Math.min(1, Math.max(0, 0.5 + e.gamma / 50)));
      my.set(Math.min(1, Math.max(0, 0.5 + (e.beta - 40) / 50)));
    };
    window.addEventListener("deviceorientation", onTilt);
    return () => window.removeEventListener("deviceorientation", onTilt);
  }, [mx, my]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { mx.set(0.5); my.set(0.5); };

  return (
    <Link
      to={panel.to}
      data-testid={panel.testid}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="group relative flex-1 md:hover:flex-[1.5] transition-[flex] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] overflow-hidden border-t md:border-t-0 md:border-l first:border-l-0 border-white/10 min-h-[38vh] md:min-h-0 [perspective:1200px]"
    >
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="absolute inset-0 will-change-transform">
        <motion.div style={{ x: imgX, y: imgY }} className="absolute -inset-[5%]">
          <motion.img
            src={panel.image}
            alt={panel.title}
            style={{ objectPosition: panel.position || "center" }}
            initial={{ scale: 1.15, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.4, delay: 0.4 + i * 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        </motion.div>
        <div className="absolute inset-0 bg-[#171914]/40 group-hover:bg-[#171914]/25 transition-colors duration-700" />
        <div className="relative h-full flex flex-col justify-end p-6 md:p-12" style={{ transform: "translateZ(60px)" }}>
          <span className="font-display text-outline text-6xl md:text-8xl leading-none">0{i + 1}</span>
          <h2 className="font-display text-5xl md:text-7xl tracking-wide mt-2 flex items-center gap-4">
            {panel.title}
            <ArrowUpRight className="text-primary w-8 h-8 md:w-12 md:h-12 opacity-0 -translate-x-3 translate-y-3 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-500" aria-hidden="true" />
          </h2>
          <p className="mt-3 text-primary font-semibold text-sm md:text-base max-w-sm [text-shadow:0_1px_10px_rgba(0,0,0,0.75)]">{panel.sub}</p>
          <div className="mt-5 h-0.5 w-0 group-hover:w-24 bg-primary transition-all duration-700" />
        </div>
      </motion.div>
    </Link>
  );
};

export default function Entry() {
  return (
    <div data-testid="entry-page" className="min-h-screen flex flex-col bg-background relative grain">
      <header className="relative z-20 flex items-center justify-between px-5 md:px-10 py-5 border-b border-white/10">
        <img src="/logo.png" alt="Infinite Siding LLC" className="h-10 md:h-12 w-auto logo-on-dark" data-testid="entry-logo" />
        <div className="flex items-center gap-5 md:gap-7">
          <Link to="/projects" data-testid="entry-projects" className="text-xs md:text-sm font-bold tracking-widest uppercase text-white/80 hover:text-primary transition-colors duration-300">
            Projects
          </Link>
          <a href="tel:+12033075222" data-testid="entry-phone" className="flex items-center gap-2 text-xs md:text-sm font-bold tracking-widest text-white/80 hover:text-primary transition-colors duration-300">
            <Phone size={15} className="text-primary" aria-hidden="true" /> (203) 307-5222
          </a>
        </div>
      </header>

      <div className="relative z-10 pt-10 md:pt-14 pb-8 px-5 md:px-10 text-center">
        <KineticLines
          lines={["WHAT ARE WE", "BUILDING TODAY?"]}
          className="font-display text-5xl md:text-8xl leading-[0.9] tracking-wide"
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-4 text-white/50 text-xs md:text-sm tracking-[0.35em] uppercase"
        >
          Choose your project type
        </motion.p>
      </div>

      <main className="relative z-10 flex-1 flex flex-col md:flex-row">
        {PANELS.map((panel, i) => (
          <TiltPanel key={panel.to} panel={panel} i={i} />
        ))}
      </main>

      <footer className="relative z-10 border-t border-white/10 px-5 md:px-10 py-4 flex flex-col md:flex-row items-center justify-between gap-2 text-[11px] tracking-[0.25em] uppercase text-white/40">
        <span>Licensed &amp; Insured</span>
        <span data-testid="entry-service-areas">Serving NY · CT · MA</span>
        <span>Free Estimates</span>
      </footer>
    </div>
  );
}
